import { Alert, Box, Chip, CircularProgress } from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  SkipNext,
} from "@mui/icons-material";
import type { ImageExtractionStatus } from "../../../types/extraction";

interface ExtractionImageStatusBarProps {
  status: ImageExtractionStatus | null | undefined;
  error?: string | null;
}

const getImageStatusIcon = (status: ImageExtractionStatus) => {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    case "processing":
      return <CircularProgress size={16} sx={{ mr: 0.5 }} />;
    case "pending":
      return <HourglassEmpty sx={{ fontSize: 16, mr: 0.5 }} />;
    case "skipped":
      return <SkipNext sx={{ fontSize: 16, mr: 0.5 }} />;
    default:
      return null;
  }
};

const getImageStatusChipSx = (status: ImageExtractionStatus) => {
  switch (status) {
    case "completed":
      return { backgroundColor: "#e6f6ef", color: "#0f6b45" };
    case "processing":
      return { backgroundColor: "#e7f1ff", color: "#0d4f8b" };
    case "pending":
      return { backgroundColor: "#fff5e6", color: "#8a5200" };
    case "failed":
      return { backgroundColor: "#feeceb", color: "#9a2218" };
    case "skipped":
      return { backgroundColor: "#eef1f4", color: "#4a5565" };
    default:
      return { backgroundColor: "#eef1f4", color: "#4a5565" };
  }
};

export function ExtractionImageStatusBar({
  status,
  error,
}: ExtractionImageStatusBarProps) {
  if (!status) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Chip
        {...(getImageStatusIcon(status) ? { icon: getImageStatusIcon(status)! } : {})}
        label={`Images: ${status.charAt(0).toUpperCase() + status.slice(1)}`}
        size="small"
        sx={{
          ...getImageStatusChipSx(status),
          borderRadius: "999px",
          fontWeight: 500,
          "& .MuiChip-icon": { color: "inherit" },
        }}
      />
      {status === "skipped" && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          No suitable images found in document.
        </Alert>
      )}
      {status === "failed" && error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
          <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: "0.875rem" }}>
            Text extraction may still have succeeded.
          </Box>
        </Alert>
      )}
    </Box>
  );
}
